/**
 * Copyright © 2026 Adnuntius AS.
 */
import {defineLocale, listLocales} from 'ngx-bootstrap/chronos';
import {BsDatepickerConfig, BsLocaleService} from 'ngx-bootstrap/datepicker';

const LOCALE_PREFIX = 'adn-network-dow-';
export const ADN_BS_DATEPICKER_TODAY_CLASS = 'adn-bs-datepicker-today';

let lastAppliedLocaleName: string | undefined;
let lastAppliedParentLocale: string | undefined;
let lastAppliedAngularLocaleId: string | undefined;

const DOY_BY_DOW: Record<number, number> = {
  0: 6,
  1: 4,
  2: 3,
  3: 2,
  4: 1,
  5: 0,
  6: 6
};

export interface AngularDatepickerLocale {
  id?: string;
  DATETIME_FORMATS?: {
    DAY?: string[];
    SHORTDAY?: string[];
    MONTH?: string[];
    SHORTMONTH?: string[];
  };
}

function normalizeStartDay(startDayCalendar: number): number {
  if (Number.isFinite(startDayCalendar) && startDayCalendar >= 0 && startDayCalendar <= 6) {
    return startDayCalendar;
  }
  return 1;
}

function resolveAngularLocaleId(angularLocale?: AngularDatepickerLocale | string): string | undefined {
  if (!angularLocale) {
    return undefined;
  }
  return typeof angularLocale === 'string' ? angularLocale : angularLocale.id;
}

function resolveRegisteredParentLocale(angularLocaleId?: string): string | undefined {
  const locales = listLocales();
  const candidates = [
    angularLocaleId,
    angularLocaleId?.replace(/_/g, '-'),
    angularLocaleId?.split(/[-_]/)[0]
  ].filter(Boolean);

  for (const candidate of candidates) {
    if (locales.indexOf(candidate) >= 0) {
      return candidate;
    }
  }
  return undefined;
}

function isValidWeekdayList(values?: string[]): values is string[] {
  return Array.isArray(values) && values.length === 7;
}

function isValidMonthList(values?: string[]): values is string[] {
  return Array.isArray(values) && values.length === 12;
}

function toWeekdaysMin(shortDays: string[]): string[] {
  return shortDays.map((label) => label.replace(/\./g, '').slice(0, 2));
}

function buildCalendarLabelsFromAngular(angularLocale?: AngularDatepickerLocale | string): Record<string, string[]> {
  if (!angularLocale || typeof angularLocale === 'string') {
    return {};
  }

  const formats = angularLocale.DATETIME_FORMATS;
  if (!formats) {
    return {};
  }

  const labels: Record<string, string[]> = {};

  if (isValidWeekdayList(formats.DAY)) {
    labels.weekdays = formats.DAY;
  }
  if (isValidWeekdayList(formats.SHORTDAY)) {
    labels.weekdaysShort = formats.SHORTDAY;
    labels.weekdaysMin = toWeekdaysMin(formats.SHORTDAY);
  }
  if (isValidMonthList(formats.MONTH)) {
    labels.months = formats.MONTH;
  }
  if (isValidMonthList(formats.SHORTMONTH)) {
    labels.monthsShort = formats.SHORTMONTH;
  }

  return labels;
}

export function toBsDateInputFormat(shortDateFormat?: string): string {
  const angularFormat = (shortDateFormat || 'd/M/yy').replace(/yy/g, 'y');
  return angularFormat
    .replace(/yyyy/g, 'YYYY')
    .replace(/yyy/g, 'YYYY')
    .replace(/\byy\b/g, 'YY')
    .replace(/\by\b/g, 'YYYY')
    .replace(/dd/g, 'DD')
    .replace(/\bd\b/g, 'D');
}

export interface AdnBsDatepickerConfigOptions {
  shortDateFormat?: string;
  today?: Date;
  todayButtonLabel?: string;
  clearButtonLabel?: string;
  showClearButton?: boolean;
}

export function createAdnBsDatepickerConfig(options: AdnBsDatepickerConfigOptions = {}): Partial<BsDatepickerConfig> {
  const today = options.today ? new Date(options.today) : new Date();
  today.setHours(0, 0, 0, 0);

  const config: Partial<BsDatepickerConfig> = {
    dateInputFormat: toBsDateInputFormat(options.shortDateFormat),
    dateCustomClasses: [{
      date: today,
      classes: [ADN_BS_DATEPICKER_TODAY_CLASS]
    }],
    selectFromOtherMonth: true,
    showTodayButton: true,
    todayPosition: 'left',
    todayButtonLabel: options.todayButtonLabel || 'Today'
  };

  if (options.showClearButton) {
    config.showClearButton = true;
    config.clearPosition = 'right';
    config.clearButtonLabel = options.clearButtonLabel || 'Clear';
  }

  return config;
}

export function applyNetworkCalendarWeekStart(
  bsLocaleService: BsLocaleService,
  startDayCalendar: number,
  angularLocale?: AngularDatepickerLocale | string
): void {
  const dow = normalizeStartDay(startDayCalendar);
  const localeName = LOCALE_PREFIX + dow;
  const angularLocaleId = resolveAngularLocaleId(angularLocale);
  const parentLocale = resolveRegisteredParentLocale(angularLocaleId);

  if (lastAppliedLocaleName === localeName
    && lastAppliedParentLocale === parentLocale
    && lastAppliedAngularLocaleId === angularLocaleId
    && bsLocaleService.currentLocale === localeName) {
    return;
  }

  const localeConfig: Record<string, unknown> = {
    week: {
      dow,
      doy: DOY_BY_DOW[dow]
    },
    ...buildCalendarLabelsFromAngular(angularLocale)
  };

  if (parentLocale) {
    localeConfig.parentLocale = parentLocale;
  }

  defineLocale(localeName, localeConfig);
  bsLocaleService.use(localeName);
  lastAppliedLocaleName = localeName;
  lastAppliedParentLocale = parentLocale;
  lastAppliedAngularLocaleId = angularLocaleId;
}
